/**
 * The Mixcloud player widget: the only way to play a show.
 *
 * The API gives out no audio - "Audio streams are not available through the
 * Mixcloud API", for licensing and because plays have to be counted - so
 * Mixcloud playback is an iframe on player-widget.mixcloud.com driven over
 * postMessage, the same arrangement as YouTube rather than the one the audio
 * element serves for every other source. See docs/MIXCLOUD.md section 2.
 *
 * Three things about widgetApi.js decide how the player uses it, all read
 * out of the bundle rather than the docs:
 *
 * - `Mixcloud.PlayerWidget(iframeEl)` takes the *element*, reads its
 *   contentWindow and sends `getApi` at once. The widget also announces
 *   `ready` when it loads and the parent asks again, so attaching before or
 *   after the iframe has loaded both work.
 * - The method and event names are not in the script: the widget sends its
 *   own API description and the script builds the object from it. So the
 *   surface typed below is the documented one, and a name that ever
 *   disappears would show up as `undefined is not a function` rather than a
 *   type error.
 * - **Rebuilding the API drops event handlers.** Every `api` message
 *   replaces the callback list behind `events.*`, and a fresh iframe `src`
 *   makes the widget announce itself again. That is why the player sets the
 *   src once and changes shows with `load()`: registering handlers a second
 *   time would need the wrapper to know the handshake had happened again,
 *   and it cannot.
 *
 * There is also no way to tear a widget down - the script adds a window
 * message listener and never removes it - which is the other reason to keep
 * one iframe for the app's lifetime.
 */

/** Where the iframe lives, and the only origin the script accepts messages from. */
export const widgetOrigin = 'https://player-widget.mixcloud.com';

const widgetApiScriptUrl = 'https://widget.mixcloud.com/media/js/widgetApi.js';

/**
 * The widget object, as the iframe describes itself. Every method answers a
 * promise because every call is a postMessage round trip.
 */
export interface MixcloudWidget {
  /** Resolves once the iframe has described its API. */
  ready: Promise<MixcloudWidget>;
  /** Swap the show without reloading the iframe. */
  load(cloudcastKey: string, startPlaying?: boolean): Promise<void>;
  play(): Promise<void>;
  pause(): Promise<void>;
  togglePlay(): Promise<void>;
  /** Seconds from the start. */
  seek(seconds: number): Promise<boolean>;
  getPosition(): Promise<number>;
  getDuration(): Promise<number>;
  getIsPaused(): Promise<boolean>;
  events: {
    /** `(position, duration)`, both in seconds, a few times a second. */
    progress: MixcloudWidgetEvent<(position: number, duration: number) => void>;
    buffering: MixcloudWidgetEvent<() => void>;
    play: MixcloudWidgetEvent<() => void>;
    pause: MixcloudWidgetEvent<() => void>;
    ended: MixcloudWidgetEvent<() => void>;
    error: MixcloudWidgetEvent<(err?: any) => void>;
  };
}

export interface MixcloudWidgetEvent<T> {
  on(handler: T): void;
  off(handler: T): void;
}

interface MixcloudGlobal {
  PlayerWidget(iframe: HTMLIFrameElement): MixcloudWidget;
  noConflict(): MixcloudGlobal;
}

declare global {
  interface Window {
    Mixcloud?: MixcloudGlobal;
  }
}

let scriptPromise: Promise<MixcloudGlobal> | undefined;

/**
 * Loads widgetApi.js once, on the first Mixcloud track. 190 KB, so not from
 * index.html and not before someone plays something.
 */
export function loadWidgetApi(): Promise<MixcloudGlobal> {
  if (scriptPromise) {
    return scriptPromise;
  }

  if (window.Mixcloud) {
    scriptPromise = Promise.resolve(window.Mixcloud);

    return scriptPromise;
  }

  scriptPromise = new Promise<MixcloudGlobal>((resolve, reject) => {
    const script = document.createElement('script');

    script.src = widgetApiScriptUrl;
    script.async = true;
    script.onload = () => {
      if (window.Mixcloud) {
        resolve(window.Mixcloud);
      } else {
        // Let a later attempt retry rather than caching the failure forever,
        // the same as loadGis.
        scriptPromise = undefined;
        reject(new Error('Mixcloud widget script loaded without an API.'));
      }
    };
    script.onerror = () => {
      scriptPromise = undefined;
      reject(new Error('Unable to load the Mixcloud player.'));
    };

    document.head.appendChild(script);
  });

  return scriptPromise;
}

/**
 * The iframe src for a show.
 *
 * `feed` is the cloudcast key, url-encoded slashes and all. `mini` is the
 * 60px bar, which is what fits a player already fixed at 100px, and it
 * carries Mixcloud's own play button, scrubber, artwork, title and link
 * back to the show - so the player adds no transport of its own for this
 * source, and hides its own title row while one plays.
 *
 * No trailing slash before the query: `/widget/iframe/?…` answers a 307 to
 * this form, and an iframe that redirects on every track change is a
 * needless round trip.
 */
export function widgetIframeUrl(cloudcastKey: string, autoplay = true) {
  return (
    `${widgetOrigin}/widget/iframe` +
    `?feed=${encodeURIComponent(cloudcastKey)}` +
    '&mini=1' +
    /*
      Not cosmetic, despite the name.

      A mini widget that has not started playing renders a *poster* - the
      show's cover art full-bleed behind an outsized play button, with the
      Mixcloud wordmark over the title - and at the 60px this frame gets, that
      arrives as a scrollbar and overlapping text. It looks broken, and it is
      exactly what a restored session shows, because a session restored from a
      restart is deliberately born paused.

      hide_cover drops that backdrop, and the paused widget renders as the
      same bar it shows while playing: thumbnail, play, title, uploader.
      Measured against mini alone, mini+light and mini+start_time, all of
      which keep the poster; hide_artwork also works but takes the thumbnail
      with it.
    */
    '&hide_cover=1' +
    (autoplay ? '&autoplay=1' : '')
  );
}
